import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PartyPopper } from 'lucide-react';
import { paths } from '@/config/paths';
import { Button } from '@/shared/components/ui/button';
import { InviteMembers } from '@/features/invites/components/invite-members';
import { Group, GroupStatus } from '@/types/api/group';
import { MembershipRole } from '@/types/api/membership';
import { getGroupQueryOptions } from '../../api/get-group';
import { useOnboardingPrefs } from '../../stores/onboarding-prefs.store';
import { useAuth } from '@/hooks/auth/use-auth';
import { OnboardingGoal } from './onboarding-goal';

/** `saleId` is the sale to go to, which can be known before the group's default sale is set */
export function OnboardingComplete({ group, saleId }: { group: Group; saleId?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const prefs = useOnboardingPrefs(group.id);
  const { context } = useAuth();
  const isLeader = Boolean(context?.membershipRoles.includes(MembershipRole.Leader));

  // The steps can be done here before the API activates the group, and until then OnboardingGuard
  // sends the sale pages back here. Mark it active in the cache, which stays fresh until the API catches up.
  // Cancel the refetch the last step started first, or it lands after this and puts back ONBOARDING.
  const goToSale = async () => {
    const { queryKey } = getGroupQueryOptions(group.id);
    await queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData(queryKey, (cached) =>
      cached && { ...cached, status: GroupStatus.Active, defaultSaleId: cached.defaultSaleId ?? saleId ?? null },
    );
    navigate(paths.sale.overview.getHref(saleId!));
  };

  return (
    <div className="flex flex-col items-center justify-center gap-6 p-4 max-w-md mx-auto h-full text-center">
      <PartyPopper className="size-16 text-primary" />

      <div className="space-y-2">
        <h2 className="text-2xl font-bold">Försäljningen är igång 🎉</h2>
        <p>Nu kan deltagarna börja sälja – varje såld produkt fyller kassan.</p>
      </div>

      <OnboardingGoal prefs={prefs} />

      <div className="flex gap-4 flex-col w-full md:w-auto md:flex-row">
        <Button onClick={goToSale}>Till försäljningen</Button>
        {isLeader && (
          <InviteMembers roles={[MembershipRole.Member]} orgId={group.id} variant={'outline'}>
            Bjud in fler deltagare
          </InviteMembers>
        )}
      </div>
    </div>
  );
}
